const RELOAD_KEY = "tfc.translation-reload";
/** One reload per page per minute: a second failure inside it is real, not a blip. */
const RELOAD_WINDOW_MS = 60_000;
/**
 * How long a reload counts as under way. A document that is still here after it did not unload
 * (Stop, Esc), so the gate stops waiting for it and the second-failure path shows the error.
 */
const RELOAD_UNDER_WAY_MS = 10_000;

/**
 * The document navigation a failed dictionary triggers, behind a seam: jsdom cannot navigate, so
 * the tests replace `reload`. `pending` is the page this document is already leaving for, and when —
 * a re-render while the reload is under way must not count as a second failure.
 */
export const documentNavigation = {
  pending: undefined as { href: string; at: number } | undefined,
  // `replace`, not `assign`: the reload stands in for the navigation that failed, so it takes that
  // history entry instead of adding a second one for the same page (audit round 9).
  reload(href: string): void {
    window.location.replace(href);
  },
};

/**
 * T956 design B (founder 2026-11-02): when a page's dictionaries fail, load that page again from
 * the server — its HTML carries the copy, and a new document starts i18next from scratch, so
 * nothing stays failed. At most once per page per minute; a second failure, or no session storage
 * to remember the first, returns false so the gate shows the translated error screen instead
 * (the `useOfflineRecovery` posture, T841).
 */
export function reloadForTranslations(path: string): boolean {
  // Assigning the current address with a `#` only scrolls within this document, so the reload
  // targets the address without it.
  const href = path.split("#")[0];
  const now = Date.now();
  const { pending } = documentNavigation;
  if (pending?.href === href && now - pending.at < RELOAD_UNDER_WAY_MS) return true;
  const key = `${RELOAD_KEY}:${href}`;
  try {
    const last = Number(window.sessionStorage.getItem(key));
    if (last > 0 && now - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(key, String(now));
  } catch {
    return false;
  }
  documentNavigation.pending = { href, at: now };
  documentNavigation.reload(href);
  return true;
}

/**
 * Until the router hydrates, nothing in the document answers Back and Forward: while the boot
 * waits on its dictionaries, and for good on a public page whose dictionaries failed (it keeps its
 * server HTML, R-T956-6) — the address would change while the page stayed. The boot arms this
 * before any dictionary loads and calls the returned disarm right before hydrating. It loads the
 * document of a traversal that changes the path or query; a fragment-only step (an in-page anchor)
 * is left to the browser.
 */
export function reloadOnHistoryTraversal(): () => void {
  let current = window.location.pathname + window.location.search;
  const onPopState = () => {
    const next = window.location.pathname + window.location.search;
    if (next === current) return;
    current = next;
    // Another path or query loads a new document even with a fragment, so the entry keeps it.
    documentNavigation.reload(next + window.location.hash);
  };
  window.addEventListener("popstate", onPopState);
  return () => window.removeEventListener("popstate", onPopState);
}
